import type { Mode } from "./lib/rsvp";

export type Subscription = {
  status: string;
  active: boolean;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
};

export type Me = {
  userId: string;
  email: string;
  name: string | null;
  isAdmin: boolean;
  subscription: Subscription | null;
};

export type CollectionStatus = "draft" | "published" | "archived";
export type Access = "free" | "paid";
export type AudioStatus = "queued" | "processing" | "ready" | "error";

export type CollectionSummary = {
  slug: string;
  title: string;
  description: string | null;
  level: string | null;
  tags: string[];
  status: CollectionStatus;
  access: Access;
  locked: boolean;
  owned: boolean;
  samplePassageId: string | null;
  passageCount: number;
  playedCount: number;
};

export type PassageSummary = {
  id: string;
  slug: string;
  title: string | null;
  position: number;
  durationMs: number | null;
  audioStatus: AudioStatus;
  playCount: number;
  lastPlayedAt: number | null;
  locked: boolean;
};

export type CollectionDetail = {
  collection: CollectionSummary;
  passages: PassageSummary[];
};

export type Segment = { text: string; fromMs: number; toMs: number };

export type Passage = {
  id: string;
  slug: string;
  title: string | null;
  collection: { slug: string; title: string };
  text: string;
  translation: string | null;
  note: string | null;
  audioStatus: AudioStatus;
  audioUrl: string | null;
  durationMs: number | null;
  timingQuality: "exact" | "aligned" | "estimated" | null;
  segments: Segment[];
  chunks: string[];
  owned: boolean;
  prevId: string | null;
  nextId: string | null;
};

export type HistoryItem = {
  passageId: string;
  passageTitle: string | null;
  collectionSlug: string;
  mode: Mode;
  createdAt: number;
  nextPassageId: string | null;
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export class SubscriptionRequiredError extends ApiError {
  constructor(readonly collection: { slug: string; title: string } | null) {
    super(402, "subscription_required");
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, { credentials: "include", ...init });
  if (res.ok) return (res.status === 204 ? undefined : await res.json()) as T;
  const body = (await res.json().catch(() => ({}))) as {
    error?: string;
    collection?: { slug: string; title: string };
  };
  if (res.status === 402) throw new SubscriptionRequiredError(body.collection ?? null);
  throw new ApiError(
    res.status,
    `Request failed (${res.status})${body.error ? `: ${body.error}` : ""}`,
  );
}

const post = <T>(path: string, body?: unknown) =>
  req<T>(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

export const getMe = () => req<Me>("/me");

export const listCollections = () => req<CollectionSummary[]>("/collections");

export const getCollection = (slug: string) =>
  req<CollectionDetail>(`/collections/${encodeURIComponent(slug)}`);

const fetchPassage = (id: string) => req<Passage>(`/passages/${encodeURIComponent(id)}`);

const prefetched = new Map<string, Promise<Passage>>();

/** Warm the next passage (DTO and mp3) so auto-advance does not stall. */
export function prefetchPassage(id: string) {
  if (prefetched.has(id)) return;
  const p = fetchPassage(id).then((passage) => {
    if (passage.audioUrl) {
      const warm = new Audio();
      warm.preload = "auto";
      warm.src = passage.audioUrl;
    }
    return passage;
  });
  p.catch(() => prefetched.delete(id));
  prefetched.set(id, p);
}

export function getPassage(id: string) {
  const p = prefetched.get(id);
  prefetched.delete(id);
  return p ?? fetchPassage(id);
}

export const recordPlay = (id: string, play: { mode: Mode; rate: number; voicedRatio?: number }) =>
  post<void>(`/passages/${encodeURIComponent(id)}/plays`, play);

export const recordStart = (id: string, mode: Mode) =>
  post<void>(`/passages/${encodeURIComponent(id)}/starts`, { mode });

export const getHistory = (limit = 10) => req<HistoryItem[]>(`/history?limit=${limit}`);

export const startCheckout = () => post<{ url: string }>("/billing/checkout");

export const openPortal = () => post<{ url: string }>("/billing/portal");

export const syncBilling = () => post<Subscription | null>("/billing/sync");

export type ScriptQuota = { limit: number; used: number; entitled: boolean };

export const getScriptQuota = () => req<ScriptQuota>("/scripts/quota");

export const createScript = (script: { text: string; title?: string }) =>
  post<{ id: string; collectionSlug: string }>("/scripts", script);

export const updateScriptChunks = (id: string, chunks: string[]) =>
  req<void>(`/scripts/${encodeURIComponent(id)}/chunks`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ chunks }),
  });

export const deleteScript = (id: string) =>
  req<void>(`/scripts/${encodeURIComponent(id)}`, { method: "DELETE" });
